/**
 * Bootstrap5TabsManager - A class for generating and manipulating Bootstrap 5 tabs
 */
export default class TabsClass {
    constructor(container, options = {}) {
        // Default options
        this.options = {
            navClass: 'nav nav-tabs',
            contentClass: 'tab-content pt-3 w-100',
            activeTab: 0,
            fade: true,
            pills: false,
            vertical: false,
            ...options
        }

        // Container element
        this.$container = container ?? $('<div>')

        // Tab collections
        this.tabs = []
        this.tabIds = []

        // Initialize the structure
        this.initialize()
    }

    /**
     * Initialize the tab structure
     * @private
     */
    initialize() {
        // Clear the container
        this.$container.empty();

        // Set up nav element
        let navClass = this.options.navClass;
        if (this.options.pills) {
            navClass = navClass.replace('nav-tabs', 'nav-pills');
        }
        if (this.options.vertical) {
            navClass += ' flex-column';
            this.$container.addClass('d-flex');
        }

        this.$navElement = $('<ul>').addClass(navClass).attr('role', 'tablist');
        this.$contentElement = $('<div>').addClass(this.options.contentClass);

        // Append elements to container
        this.$container.append(this.$navElement).append(this.$contentElement);
    }

    /**
     * Add a new tab
     * @param {string} title - The tab title
     * @param {string|jQuery} content - The tab content (can be HTML string or jQuery object)
     * @param {Object} tabOptions - Options for this specific tab
     * @returns {number} - The index of the newly added tab
     */
    addTab(title, content, tabOptions = {}) {
        const tabId = `tab-${Math.random().toString(36).substring(2, 9)}`;
        const contentId = `content-${tabId}`;

        const isActive = this.tabs.length === this.options.activeTab;

        // Create tab nav item
        const $tabNav = $('<li>').addClass('nav-item').attr('role', 'presentation');

        const $tabLink = $('<button>')
            .addClass(`nav-link ${isActive ? 'active' : ''}`)
            .attr({
                'id': tabId,
                'data-bs-toggle': 'tab',
                'data-bs-target': `#${contentId}`,
                'role': 'tab',
                'aria-controls': contentId,
                'aria-selected': isActive ? 'true' : 'false',
                'type': 'button'
            })
            .html(title);

        $tabNav.append($tabLink);
        this.$navElement.append($tabNav);

        // Create tab content
        const $tabContent = $('<div>')
            .addClass(`tab-pane ${isActive ? 'active' : ''} ${this.options.fade ? 'fade' : ''} ${isActive && this.options.fade ? 'show' : ''}`)
            .attr({
                'id': contentId,
                'role': 'tabpanel',
                'aria-labelledby': tabId
            });

        // Add content to the tab
        if (typeof content === 'string') {
            $tabContent.html(content);
        } else if (content instanceof jQuery) {
            $tabContent.append(content);
        } else if (content instanceof HTMLElement) {
            $tabContent.append($(content));
        }

        this.$contentElement.append($tabContent);

        // Store tab information
        this.tabs.push({
            index: this.tabs.length,
            id: tabId,
            contentId: contentId,
            title: title,
            $navElement: $tabNav,
            $contentElement: $tabContent,
            options: tabOptions
        });

        this.tabIds.push(tabId);

        // Return the index of the newly added tab
        return this.tabs.length - 1;
    }

    /**
     * Remove a tab by index
     * @param {number} index - The index of the tab to remove
     */
    removeTab(index) {
        if (index < 0 || index >= this.tabs.length) {
            throw new Error('Tab index out of bounds');
        }

        const tab = this.tabs[index];
        tab.$navElement.remove();
        tab.$contentElement.remove();

        // Remove from collections
        this.tabs.splice(index, 1);
        this.tabIds.splice(index, 1);

        // Update indexes
        for (let i = 0; i < this.tabs.length; i++) {
            this.tabs[i].index = i;
        }

        // If we removed the active tab, activate the first tab
        if (this.tabs.length > 0 && this.$container.find('.nav-link.active').length === 0) {
            this.activateTab(0);
        }
    }

    /**
     * Activate a tab by index
     * @param {number} index - The index of the tab to activate
     */
    activateTab(index) {
        if (index < 0 || index >= this.tabs.length) {
            throw new Error('Tab index out of bounds');
        }

        const tab = this.tabs[index];

        // Deactivate all tabs
        this.$container.find('.nav-link').removeClass('active').attr('aria-selected', 'false');
        this.$container.find('.tab-pane').removeClass('active show');

        // Activate the selected tab
        $(`#${tab.id}`).addClass('active').attr('aria-selected', 'true');
        $(`#${tab.contentId}`).addClass('active');

        if (this.options.fade) {
            $(`#${tab.contentId}`).addClass('show');
        }

        // Trigger the Bootstrap tab events manually if needed
        // This is useful if you're not using Bootstrap's JS
        $(`#${tab.id}`).trigger('shown.bs.tab');
    }

    /**
     * Get a tab by index
     * @param {number} index - The index of the tab
     * @returns {Object} - The tab object
     */
    getTab(index) {
        if (index < 0 || index >= this.tabs.length) {
            throw new Error('Tab index out of bounds');
        }
        return this.tabs[index];
    }

    /**
     * Update a tab's title
     * @param {number} index - The index of the tab
     * @param {string} newTitle - The new title
     */
    updateTabTitle(index, newTitle) {
        if (index < 0 || index >= this.tabs.length) {
            throw new Error('Tab index out of bounds');
        }

        const tab = this.tabs[index];
        $(`#${tab.id}`).html(newTitle);
        tab.title = newTitle;
    }

    /**
     * Update a tab's content
     * @param {number} index - The index of the tab
     * @param {string|jQuery|HTMLElement} newContent - The new content
     */
    updateTabContent(index, newContent) {
        if (index < 0 || index >= this.tabs.length) {
            throw new Error('Tab index out of bounds');
        }

        const tab = this.tabs[index];
        const $contentElement = $(`#${tab.contentId}`);

        $contentElement.empty();

        if (typeof newContent === 'string') {
            $contentElement.html(newContent);
        } else if (newContent instanceof jQuery) {
            $contentElement.append(newContent);
        } else if (newContent instanceof HTMLElement) {
            $contentElement.append($(newContent));
        }
    }

    /**
     * Add event listener for tab events
     * @param {string} event - The event to listen for (shown.bs.tab, show.bs.tab, etc.)
     * @param {Function} callback - The callback function
     */
    on(event, callback) {
        this.$container.on(event, callback);
    }

    /**
     * Convert tabs to pills style
     * @param {boolean} isPills - Whether to use pills style
     */
    setPills(isPills) {
        this.options.pills = isPills;
        if (isPills) {
            this.$navElement.removeClass('nav-tabs').addClass('nav-pills');
        } else {
            this.$navElement.removeClass('nav-pills').addClass('nav-tabs');
        }
    }

    /**
     * Enable or disable fade effect
     * @param {boolean} hasFade - Whether to use fade effect
     */
    setFade(hasFade) {
        this.options.fade = hasFade;

        const $panes = this.$container.find('.tab-pane');
        if (hasFade) {
            $panes.addClass('fade');
            this.$container.find('.tab-pane.active').addClass('show');
        } else {
            $panes.removeClass('fade show');
        }
    }

    /**
     * Set vertical orientation
     * @param {boolean} isVertical - Whether tabs should be vertical
     */
    setVertical(isVertical) {
        this.options.vertical = isVertical;

        if (isVertical) {
            this.$navElement.addClass('flex-column');
            this.$container.addClass('d-flex');
        } else {
            this.$navElement.removeClass('flex-column');
            this.$container.removeClass('d-flex');
        }
    }

    /**
     * Completely destroy the tabs and restore the container to its original state
     */
    destroy() {
        this.$container.empty();
        this.tabs = [];
        this.tabIds = [];
    }

    get (){
        return this.$container
    }
}
